/**
 * MemberRegisterPage - Enhanced step-by-step registration with church hierarchy
 * Modern Cathedral Design with Province → District → Assembly selection
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '@/lib/api';
import { cn } from '@/lib/utils';

// UI Components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

// Icons
import {
  User, Mail, Phone, Lock, Church, MapPin, ChevronRight, ChevronLeft,
  CheckCircle2, Loader2, Eye, EyeOff, Search, BookOpen, Sparkles,
  Building2, Users, Calendar, ArrowRight, Check, ChevronsUpDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============== Step 1: Personal Information ==============
const PersonalInfoStep = ({ data, onChange, errors }) => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mx-auto mb-4">
          <User className="h-7 w-7 text-white" />
        </div>
        <h2 className="text-2xl font-heading font-bold text-slate-900">Personal Information</h2>
        <p className="text-slate-500 mt-2">Let's get to know you better</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="first_name">First Name *</Label>
          <Input
            id="first_name"
            value={data.first_name}
            onChange={(e) => onChange({ ...data, first_name: e.target.value })}
            placeholder="John"
            className={cn("h-12 rounded-xl", errors?.first_name && "border-red-500")}
          />
          {errors?.first_name && <p className="text-sm text-red-500">{errors.first_name}</p>}
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="last_name">Last Name *</Label>
          <Input
            id="last_name"
            value={data.last_name}
            onChange={(e) => onChange({ ...data, last_name: e.target.value })}
            placeholder="Doe"
            className={cn("h-12 rounded-xl", errors?.last_name && "border-red-500")}
          />
          {errors?.last_name && <p className="text-sm text-red-500">{errors.last_name}</p>}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email Address *</Label>
        <div className="relative">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input
            id="email"
            type="email"
            value={data.email}
            onChange={(e) => onChange({ ...data, email: e.target.value })}
            placeholder="john@example.com"
            className={cn("h-12 pl-12 rounded-xl", errors?.email && "border-red-500")}
          />
        </div>
        {errors?.email && <p className="text-sm text-red-500">{errors.email}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone">Phone Number *</Label>
        <div className="relative">
          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input
            id="phone"
            type="tel"
            value={data.phone}
            onChange={(e) => onChange({ ...data, phone: e.target.value })}
            placeholder="+1 (555) 123-4567"
            className={cn("h-12 pl-12 rounded-xl", errors?.phone && "border-red-500")}
          />
        </div>
        {errors?.phone && <p className="text-sm text-red-500">{errors.phone}</p>}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Gender</Label>
          <Select value={data.gender} onValueChange={(v) => onChange({ ...data, gender: v })}>
            <SelectTrigger className="h-12 rounded-xl">
              <SelectValue placeholder="Select gender" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="male">Male</SelectItem>
              <SelectItem value="female">Female</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="dob">Date of Birth</Label>
          <Input
            id="dob"
            type="date"
            value={data.date_of_birth}
            onChange={(e) => onChange({ ...data, date_of_birth: e.target.value })}
            className="h-12 rounded-xl"
          />
        </div>
      </div>
    </div>
  );
};

// ============== Step 2: Church Connection ==============
const ChurchConnectionStep = ({ data, onChange, errors, provinceEnabled = true }) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch church hierarchy data
  const { data: provinces = [] } = useQuery({
    queryKey: ['church-provinces'],
    queryFn: () => api.get('/churches/provinces').then(r => r.data).catch(() => []),
  });

  const { data: districts = [] } = useQuery({
    queryKey: ['church-districts', data.province_id],
    queryFn: () => api.get(`/churches/districts?province_id=${data.province_id}`).then(r => r.data).catch(() => []),
    enabled: !!data.province_id,
  });

  const { data: assemblies = [] } = useQuery({
    queryKey: ['church-assemblies', data.district_id],
    queryFn: () => api.get(`/churches/assemblies?district_id=${data.district_id}`).then(r => r.data).catch(() => []),
    enabled: !!data.district_id,
  });

  // Fetch all churches for search
  const { data: allChurches = [] } = useQuery({
    queryKey: ['all-churches'],
    queryFn: () => api.get('/churches').then(r => r.data).catch(() => []),
  });

  const filteredChurches = allChurches.filter(church =>
    church.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    church.city?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleChurchSelect = (church) => {
    onChange({
      ...data,
      church_id: church.id,
      church_name: church.name,
      province_id: church.province_id || '',
      district_id: church.district_id || '',
    });
    setSearchOpen(false);
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mx-auto mb-4">
          <Church className="h-7 w-7 text-white" />
        </div>
        <h2 className="text-2xl font-heading font-bold text-slate-900">Church Connection</h2>
        <p className="text-slate-500 mt-2">Connect with your local church family</p>
      </div>

      {/* Search for Church */}
      <div className="space-y-2">
        <Label>Search for Your Church</Label>
        <Popover open={searchOpen} onOpenChange={setSearchOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              className="w-full h-12 justify-between rounded-xl"
            >
              {data.church_name || "Search by church name or location..."}
              <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-0" align="start">
            <Command>
              <CommandInput 
                placeholder="Search churches..." 
                value={searchQuery}
                onValueChange={setSearchQuery}
              />
              <CommandList>
                <CommandEmpty>No church found.</CommandEmpty>
                <CommandGroup heading="Churches">
                  {filteredChurches.slice(0, 10).map((church) => (
                    <CommandItem
                      key={church.id}
                      value={church.name}
                      onSelect={() => handleChurchSelect(church)}
                    >
                      <Church className="mr-2 h-4 w-4" />
                      <div>
                        <div className="font-medium">{church.name}</div>
                        <div className="text-xs text-slate-500">{church.city}, {church.country}</div>
                      </div>
                      {data.church_id === church.id && (
                        <Check className="ml-auto h-4 w-4" />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <div className="relative py-4">
        <Separator />
        <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-4 text-sm text-slate-400">
          or select from hierarchy
        </span>
      </div>

      {/* Province Selection - only shown when province toggle is enabled */}
      {provinceEnabled && (
        <>
          <div className="space-y-2">
            <Label>Province / Region</Label>
            <Select 
              value={data.province_id} 
              onValueChange={(v) => onChange({ ...data, province_id: v, district_id: '', church_id: '', church_name: '' })}
            >
              <SelectTrigger className="h-12 rounded-xl">
                <SelectValue placeholder="Select province" />
              </SelectTrigger>
              <SelectContent>
                {provinces.map((province) => (
                  <SelectItem key={province.id} value={province.id}>
                    {province.name}
                  </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* District Selection */}
      {data.province_id && (
        <div className="space-y-2">
          <Label>District</Label>
          <Select 
            value={data.district_id} 
            onValueChange={(v) => onChange({ ...data, district_id: v, church_id: '', church_name: '' })}
          >
            <SelectTrigger className="h-12 rounded-xl">
              <SelectValue placeholder="Select district" />
            </SelectTrigger>
            <SelectContent>
              {districts.map((district) => (
                <SelectItem key={district.id} value={district.id}>
                  {district.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Assembly Selection */}
      {data.district_id && (
        <div className="space-y-2">
          <Label>Assembly / Local Church *</Label>
          <Select 
            value={data.church_id} 
            onValueChange={(v) => {
              const selected = assemblies.find(a => a.id === v);
              onChange({ ...data, church_id: v, church_name: selected?.name || '' });
            }}
          >
            <SelectTrigger className={cn("h-12 rounded-xl", errors?.church_id && "border-red-500")}>
              <SelectValue placeholder="Select your local church" />
            </SelectTrigger>
            <SelectContent>
              {assemblies.map((assembly) => (
                <SelectItem key={assembly.id} value={assembly.id}>
                  {assembly.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors?.church_id && <p className="text-sm text-red-500">{errors.church_id}</p>}
        </div>
      )}
      </>
      )}

      {/* Custom Church Entry */}
      <div className="p-4 bg-slate-50 rounded-xl">
        <Label className="text-slate-600">Can't find your church?</Label>
        <Input
          value={data.custom_church || ''}
          onChange={(e) => onChange({ ...data, custom_church: e.target.value })}
          placeholder="Enter your church name manually"
          className="mt-2 h-12 rounded-xl"
        />
      </div>
    </div>
  );
};

// ============== Step 3: Account Setup ==============
const AccountSetupStep = ({ data, onChange, errors }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mx-auto mb-4">
          <Lock className="h-7 w-7 text-white" />
        </div>
        <h2 className="text-2xl font-heading font-bold text-slate-900">Create Your Account</h2>
        <p className="text-slate-500 mt-2">Set up your login credentials</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">Password *</Label>
        <div className="relative">
          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            value={data.password}
            onChange={(e) => onChange({ ...data, password: e.target.value })}
            placeholder="Create a strong password"
            className={cn("h-12 pl-12 pr-12 rounded-xl", errors?.password && "border-red-500")}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400"
          >
            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        </div>
        {errors?.password && <p className="text-sm text-red-500">{errors.password}</p>}
        <p className="text-xs text-slate-500">At least 8 characters with a mix of letters and numbers</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="confirm_password">Confirm Password *</Label>
        <div className="relative">
          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input
            id="confirm_password"
            type={showConfirm ? 'text' : 'password'}
            value={data.confirm_password}
            onChange={(e) => onChange({ ...data, confirm_password: e.target.value })}
            placeholder="Confirm your password"
            className={cn("h-12 pl-12 pr-12 rounded-xl", errors?.confirm_password && "border-red-500")}
          />
          <button
            type="button"
            onClick={() => setShowConfirm(!showConfirm)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400"
          >
            {showConfirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        </div>
        {errors?.confirm_password && <p className="text-sm text-red-500">{errors.confirm_password}</p>}
      </div>

      {/* Terms Agreement */}
      <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl">
        <Checkbox
          id="agree_terms"
          checked={data.agree_terms}
          onCheckedChange={(checked) => onChange({ ...data, agree_terms: checked })}
          className="mt-1"
        />
        <Label htmlFor="agree_terms" className="text-sm text-slate-600 cursor-pointer leading-relaxed">
          I agree to the Terms of Service and Privacy Policy. I understand my information will be used 
          to create my church membership account.
        </Label>
      </div>
      {errors?.agree_terms && <p className="text-sm text-red-500">{errors.agree_terms}</p>}
    </div>
  );
};

// ============== Step 4: Confirmation ==============
const ConfirmationStep = ({ data, application }) => {
  const pending = application?.status === 'pending';
  const withAssembly = Boolean(application?.awaiting_assembly);
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mx-auto mb-4 animate-bounce">
          <CheckCircle2 className="h-10 w-10 text-white" />
        </div>
        <h2 className="text-2xl font-heading font-bold text-slate-900">
          {pending ? 'Your application is in' : 'Welcome to the Family!'}
        </h2>
        <p className="text-slate-500 mt-2" data-testid="register-confirmation-note">
          {pending
            ? (withAssembly
                ? `Your account is ready, and ${data.church_name || 'your assembly'} now has your membership application to review.`
                : 'Your account is ready. We could not match the church you named, so the head office will post your application to the right assembly.')
            : 'Your account has been created successfully'}
        </p>
      </div>

      <Card className="bg-gradient-to-br from-slate-50 to-slate-100">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
              <User className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <div className="font-medium text-slate-900">{data.first_name} {data.last_name}</div>
              <div className="text-sm text-slate-500">{data.email}</div>
            </div>
          </div>
          
          {data.church_name && (
            <div className="flex items-center gap-4 pt-4 border-t">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <Church className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <div className="font-medium text-slate-900">{data.church_name}</div>
                <div className="text-sm text-slate-500">
                  {pending ? 'Membership pending their review' : 'Your local church'}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-4">
        <Card className="p-4 text-center">
          <BookOpen className="h-8 w-8 text-indigo-500 mx-auto mb-2" />
          <div className="font-medium text-slate-900">Read Bible</div>
          <p className="text-xs text-slate-500 mt-1">Start your daily reading</p>
        </Card>
        <Card className="p-4 text-center">
          <Calendar className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
          <div className="font-medium text-slate-900">Reading Plans</div>
          <p className="text-xs text-slate-500 mt-1">Join a plan today</p>
        </Card>
      </div>
    </div>
  );
};

// ============== Main Component ==============
const MemberRegisterPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    gender: '',
    date_of_birth: '',
    province_id: '',
    district_id: '',
    church_id: '',
    church_name: '',
    custom_church: '',
    password: '',
    confirm_password: '',
    agree_terms: false,
  });
  const [errors, setErrors] = useState({});
  const [isComplete, setIsComplete] = useState(false);
  const [application, setApplication] = useState(null);

  const steps = [
    { id: 'personal', title: 'Personal Info', icon: User },
    { id: 'church', title: 'Church', icon: Church },
    { id: 'account', title: 'Account', icon: Lock },
  ];

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/member/register', {
        name: `${data.first_name} ${data.last_name}`,
        email: data.email,
        phone: data.phone,
        password: data.password,
        gender: data.gender,
        date_of_birth: data.date_of_birth,
        province_id: data.province_id,
        district_id: data.district_id,
        church_id: data.church_id || null,
        church_name: data.church_name || data.custom_church,
      });
      return response.data;
    },
    onSuccess: (res) => {
      setApplication(res?.application || null);
      setIsComplete(true);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    },
  });

  // Validation
  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
      if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email';
      }
      if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    }

    if (step === 2) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
      if (formData.password !== formData.confirm_password) {
        newErrors.confirm_password = 'Passwords do not match';
      }
      if (!formData.agree_terms) {
        newErrors.agree_terms = 'You must agree to the terms';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        // Submit registration
        registerMutation.mutate(formData);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const progressPercent = isComplete ? 100 : ((currentStep + 1) / steps.length) * 100;

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image */}
      <div className="hidden lg:flex lg:w-2/5 relative">
        <img
          src="https://images.unsplash.com/photo-1438232992991-995b7058bbb3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
          alt="Church Community"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-emerald-900/80 to-slate-900/90" />
        
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <div className="max-w-md">
            <div className="w-12 h-1 bg-gold mb-6" />
            <blockquote className="font-heading text-3xl leading-relaxed mb-4">
              "So in Christ we, though many, form one body."
            </blockquote>
            <cite className="text-gold font-medium">— Romans 12:5</cite>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-gradient-to-b from-slate-50 to-white">
        <div className="w-full max-w-lg">
          {/* Progress */}
          {!isComplete && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                {steps.map((step, idx) => (
                  <div key={step.id} className="flex items-center">
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center transition-all",
                      idx < currentStep ? "bg-emerald-500 text-white" :
                      idx === currentStep ? "bg-indigo-500 text-white ring-4 ring-indigo-100" :
                      "bg-slate-200 text-slate-500"
                    )}>
                      {idx < currentStep ? <Check className="h-5 w-5" /> : <step.icon className="h-5 w-5" />}
                    </div>
                    {idx < steps.length - 1 && (
                      <div className={cn(
                        "w-16 sm:w-24 h-1 mx-2",
                        idx < currentStep ? "bg-emerald-500" : "bg-slate-200"
                      )} />
                    )}
                  </div>
                ))}
              </div>
              <Progress value={progressPercent} className="h-1" />
            </div>
          )}

          {/* Step Content */}
          <Card className="border-0 shadow-xl">
            <CardContent className="p-8">
              {isComplete ? (
                <ConfirmationStep data={formData} application={application} />
              ) : (
                <>
                  {currentStep === 0 && (
                    <PersonalInfoStep data={formData} onChange={setFormData} errors={errors} />
                  )}
                  {currentStep === 1 && (
                    <ChurchConnectionStep data={formData} onChange={setFormData} errors={errors} />
                  )}
                  {currentStep === 2 && (
                    <AccountSetupStep data={formData} onChange={setFormData} errors={errors} />
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Navigation */}
          {!isComplete && (
            <div className="flex items-center justify-between mt-6">
              <Button
                variant="ghost"
                onClick={handleBack}
                disabled={currentStep === 0}
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Back
              </Button>

              <Button
                onClick={handleNext}
                disabled={registerMutation.isPending}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {registerMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : currentStep === steps.length - 1 ? (
                  <>Create Account <CheckCircle2 className="h-4 w-4 ml-2" /></>
                ) : (
                  <>Continue <ChevronRight className="h-4 w-4 ml-1" /></>
                )}
              </Button>
            </div>
          )}

          {/* Go to Login / Dashboard */}
          {isComplete && (
            <div className="flex gap-4 mt-6">
              <Button variant="outline" className="flex-1" onClick={() => navigate('/login?type=member')}>
                Sign In
              </Button>
              <Button className="flex-1 bg-indigo-600 hover:bg-indigo-700" onClick={() => navigate('/member')}>
                Go to Dashboard <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Already have account */}
          {!isComplete && (
            <div className="text-center mt-6">
              <span className="text-slate-500">Already have an account? </span>
              <button
                onClick={() => navigate('/login?type=member')}
                className="text-indigo-600 font-medium hover:underline"
              >
                Sign In
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MemberRegisterPage;
